<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin appointments-dialog">
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ dialogTitle }}
        </div>
      </q-card-section>
      <q-card-section class="row">
        <q-table
          :rows="currentSessionActivities"
          :columns="columns"
          :pagination="{
            rowsPerPage: 50,
          }"
          :loading="isActivitiesLoading"
          class="col-12"
        >
          <template #body-cell-transferStatus="props">
            <q-td :props="props">
              <q-icon
                v-if="props.row.transferStatus === 'invalid'"
                name="warning"
                size="sm"
                color="warning"
              >
                <q-tooltip>
                  {{ props.row.errorMessage }}
                </q-tooltip>
              </q-icon>
              <q-icon
                v-else-if="props.row.transferStatus === 'pending'"
                name="pending"
                size="sm"
                color="grey-5"
              />
              <q-icon
                v-else-if="props.row.transferStatus === 'success'"
                name="published_with_changes"
                size="sm"
                color="positive"
              />
              <q-btn
                v-else-if="props.row.transferStatus === 'failed'"
                icon="error"
                size="md"
                color="negative"
                flat
                round
                dense
                @click="onShowError(props.row.errorMessage)"
              />
              <q-spinner
                v-else-if="props.row.transferStatus === 'transferring'"
                size="sm"
                color="primary"
              />
            </q-td>
          </template>
        </q-table>
      </q-card-section>
      <q-card-actions class="row">
        <q-space />
        <q-btn
          color="primary"
          :loading="showSpinner"
          :label="$t('common.buttonClose')"
          flat
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
<script>
import { documentId, Timestamp } from 'firebase/firestore';
import { chain, chunk, findIndex, get, map, set } from 'lodash';
import { mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionExportMixin from 'src/mixins/SessionExportMixin';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import AppUtils from 'src/utils/AppUtils';
import { logEvent } from 'src/utils/LogEvent';

const SESSION_ACTIVITIES_TRANSFER_STATUSES = {
  INVALID: 'invalid',
  PENDING: 'pending',
  TRANSFERRING: 'transferring',
  SUCCESS: 'success',
  FAILED: 'failed',
};

export default {
  name: 'BulkTransferSessionActivities',

  mixins: [Integrations, Interactions, SessionExportMixin],

  props: {

    sessionActivities: {
      type: Array,
      required: true,
    },

  },

  emits: ['hide', 'ok'],

  setup() {
    const { getLocaleString } = useTime();
    return { getLocaleString };
  },

  data() {
    return {
      showSpinner: false,

      currentSessionActivities: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'allowImmediateSync']),
    ...mapState(clients, ['clients']),

    dialogTitle() {
      return this.$t(
        'sessions.modals.bulkTransfer.textTitle',
        { integration: this.getIntegrationName(get(this.connectedClinic, 'integration')) },
      );
    },

    isActivitiesLoading() {
      return this.currentSessionActivities.length === 0;
    }, // isActivitiesLoading

    columns() {
      return [
        {
          name: 'transferStatus',
          label: '',
          field: 'transferStatus',
          align: 'center',
        },
        {
          name: 'client',
          label: this.$t('history.tableCols.client'),
          field: 'client',
          align: 'left',
          format: (clientId) => (chain(this.clients).find({ id: clientId }).get('clientName', '').value()),
        },
        {
          name: 'userName',
          label: this.$t('history.tableCols.userName'),
          field: 'userName',
          align: 'left',
        },
        {
          name: 'startTimestamp',
          label: this.$t('history.tableCols.startTimestamp'),
          align: 'left',
          field: 'startTimestamp',
          format: (startTimestamp) => ((startTimestamp instanceof Timestamp)
            ? this.getLocaleString(startTimestamp)
            : ''),
          sortable: true,
        },
        {
          name: 'endTimestamp',
          label: this.$t('history.tableCols.endTimestamp'),
          align: 'left',
          field: 'endTimestamp',
          format: (endTimestamp) => ((endTimestamp instanceof Timestamp)
            ? this.getLocaleString(endTimestamp)
            : ''),
          sortable: true,
        },
        {
          name: 'sessionName',
          label: this.$t('history.tableCols.sessionName'),
          align: 'left',
          field: 'sessionName',
          sortable: false,
        },
        {
          name: 'integrationReference',
          label: this.$t(
            'history.tableCols.integrationReference',
            { integration: this.getIntegrationName(get(this.connectedClinic, 'integration')) },
          ),
          align: 'left',
          field: 'appointmentData',
          format: (appointmentData) => (appointmentData ? get(appointmentData, 'integrationReference', '') : ''),
          sortable: false,
          requireIntegration: true,
        },
      ];
    },
  },

  methods: {

    // following method is REQUIRED
    // (don't change its name --> "show")
    async show() {
      this.$refs.dialog.show();
      this.currentSessionActivities = await this.prepareSessionActivities();
      this.transferSessionActivities();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onOKClick() {
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      const successfulTransfers = chain(this.currentSessionActivities)
        .filter({ transferStatus: SESSION_ACTIVITIES_TRANSFER_STATUSES.SUCCESS })
        .size()
        .value();
      this.$emit('ok', successfulTransfers);
      // or with payload: this.$emit('ok', { ... })

      // then hiding dialog
      this.hide();
    },

    onShowError(errorMessage) {
      this.alertDialog('', errorMessage);
    },

    async transferSessionActivities() {
      try {
        this.showSpinner = true;

        const pendingSessionActivities = this.currentSessionActivities
          .filter(({ transferStatus }) => transferStatus === SESSION_ACTIVITIES_TRANSFER_STATUSES.PENDING);

        if (!pendingSessionActivities.length) {
          return;
        }

        // lumary fails with rate limit errors if multiple transfers are done concurrently,
        // so it's better to not do more than 1 transfer at a time for lumary
        const MAX_CONCURRENT_TRANSFERS = this.connectedClinic?.integration === 'lumary' ? 1 : 10;

        const chunks = chunk(pendingSessionActivities, MAX_CONCURRENT_TRANSFERS);

        // using for-of loop to ensure that the chunks are processed sequentially
        // eslint-disable-next-line no-restricted-syntax
        for (const sessionActivities of chunks) {
          // eslint-disable-next-line no-await-in-loop
          await AppUtils.allSettled(
            sessionActivities.map((sessionActivity) => this.transferSessionActivity(sessionActivity)),
            this.handleTransferError,
          );
        }
      } finally {
        this.showSpinner = false;
      }
    },

    async transferSessionActivity(sessionActivity) {
      const sessionActivityIdx = findIndex(this.currentSessionActivities, { id: sessionActivity.id });

      if (sessionActivityIdx === -1) {
        return;
      }

      if (this.currentSessionActivities[sessionActivityIdx].transferStatus !== 'pending') {
        return;
      }

      try {
        set(this.currentSessionActivities, [sessionActivityIdx, 'transferStatus'], 'transferring');
        await this.transferSessionActivityNote(sessionActivity);
        set(this.currentSessionActivities, [sessionActivityIdx, 'transferStatus'], 'success');
      } catch (err) {
        set(this.currentSessionActivities, [sessionActivityIdx, 'transferStatus'], 'failed');
        set(this.currentSessionActivities, [sessionActivityIdx, 'errorMessage'], err.message);
        throw err;
      }
    },

    async transferSessionActivityNote(sessionActivity) {
      const { base64pdf, senderInfo } = await this.getSessionActivityNotesDataToTransfer(sessionActivity);

      return this.transferSessionToIntegration(
        sessionActivity.appointment,
        sessionActivity,
        base64pdf,
        this.getSessionUrl(sessionActivity.session, sessionActivity.client),
        senderInfo,
      );
    }, // transferSessionActivityNote()

    async prepareSessionActivities() {
      const activitiesIds = this.sessionActivities.map((activity) => activity.id).filter(Boolean);
      const chunkedActivitiesIds = chunk(activitiesIds, 10);

      const sessionActivitiesDb = new SessionActivitiesDB();
      const loadActivitiesActions = chunkedActivitiesIds.map(async (activityIdsChunk, idx) => {
        try {
          return await sessionActivitiesDb.readAll([
            ['clinic', '==', this.connectedClinic.id],
            [documentId(), 'in', activityIdsChunk],
          ]);
        } catch (err) {
          console.error(err.message, { error: err, activities: activityIdsChunk });
          logEvent(err, {
            context: {
              activities: activityIdsChunk,
            },
          });

          return this.sessionActivities
            .filter((activity) => activityIdsChunk.includes(activity.id))
            .map((failedActivity) => ({
              ...failedActivity,
              transferStatus: SESSION_ACTIVITIES_TRANSFER_STATUSES.FAILED,
              errorMessage: this.$t('sessions.modals.bulkTransfer.errorCouldNotLoadActivity'),
            }));
        }
      });

      const loadedActivities = (await Promise.all(loadActivitiesActions)).flat();

      return map(
        loadedActivities,
        (sessionActivity) => ({
          ...sessionActivity,
          transferStatus: this.getTransferStatus(sessionActivity),
          errorMessage: this.getErrorMessage(sessionActivity),
        }),
      );
    },

    /**
     * @param {Object} sessionActivity
     * @return {string}
     */
    getTransferStatus(sessionActivity) {
      return (this.getErrorMessage(sessionActivity) === null)
        ? SESSION_ACTIVITIES_TRANSFER_STATUSES.PENDING
        : sessionActivity.transferStatus || SESSION_ACTIVITIES_TRANSFER_STATUSES.INVALID;
    }, // getTransferStatus

    /**
     *
     * @param {Object} sessionActivity
     * @return {string | null}
     */
    getErrorMessage(sessionActivity) {
      if (sessionActivity.errorMessage) {
        return sessionActivity.errorMessage;
      }

      if (
        sessionActivity.status === 'rendered'
        || (
          sessionActivity.status === 'pending-signatures'
          && sessionActivity.renderedTimestamp
        )
      ) {
        return this.$t('sessions.modals.bulkTransfer.errorAlreadySent');
      }

      if (
        sessionActivity.status !== 'closed'
        && !(
          this.allowImmediateSync
          && sessionActivity.status === 'pending-signatures'
        )
      ) {
        return this.$t('sessions.modals.bulkTransfer.errorNotClosed');
      }

      if (!sessionActivity.appointment && !this.canTransferStaffActivityWithoutLinkedAppointment(sessionActivity)) {
        return this.$t('sessions.modals.bulkTransfer.errorNoAppointment');
      }

      return null;
    },

  },
};
</script>
<style scoped>
.appointments-dialog {
  min-width:80vw;
}
</style>
